// How the Stock screens show and read a quantity (spec/13 §6.4, ADR-0040 §2): every quantity on the wire is a whole
// number in the item's base unit — `each`, `g` or `ml` — and the console shows grams past 1 000 as kg and millilitres
// past 1 000 as L, for display only. Typed quantities come back the other way by string arithmetic, never a float:
// "3.25" kg is 3 250 g because the digits are split and padded, not because 3.25 was multiplied by 1 000.
//
// A unit cost (`StockUnitCost`) is an exact pair — an amount for a number of base units — shown per kg, per L or each
// by multiplying in integers and rounding half away from zero once, for display only (the contract's own rule).

import type { Money, StockBaseUnit, StockItemPack, StockUnitCost } from '@clavian/api-client';

/** The unit a quantity is shown or typed in: the base units, and kg and L for grams and millilitres past 1 000. */
export type DisplayUnit = 'each' | 'g' | 'kg' | 'ml' | 'l';

/** How many base units one display unit holds. */
const FACTOR: Readonly<Record<DisplayUnit, number>> = { each: 1, g: 1, kg: 1000, ml: 1, l: 1000 };

/** The display units a base unit is typed in: grams or kilograms, millilitres or litres, or whole units. */
export function inputUnitsFor(baseUnit: StockBaseUnit): readonly DisplayUnit[] {
  if (baseUnit === 'g') return ['kg', 'g'];
  if (baseUnit === 'ml') return ['l', 'ml'];
  return ['each'];
}

/** The larger unit of a base unit (kg for g, L for ml), or the base unit itself. */
function largeUnitOf(baseUnit: StockBaseUnit): DisplayUnit {
  return baseUnit === 'g' ? 'kg' : baseUnit === 'ml' ? 'l' : 'each';
}

/**
 * The unit a quantity of this item is most naturally typed in: kg or L when the item's packs or its par level run to
 * a thousand base units or more ("5 kg bag", par 3 000 g), grams or millilitres otherwise, and whole units for `each`.
 */
export function naturalInputUnit(baseUnit: StockBaseUnit, typical: readonly number[] = []): DisplayUnit {
  if (baseUnit === 'each') return 'each';
  return typical.some((quantity) => Math.abs(quantity) >= 1000) ? largeUnitOf(baseUnit) : baseUnit;
}

/** A quantity's parts as the screens set them: the sign, the digits (an exact decimal string) and the unit. */
export interface QuantityParts {
  readonly negative: boolean;
  /** Digits only, no sign, no grouping: "3.2", "850", "14". */
  readonly digits: string;
  readonly unit: DisplayUnit;
}

/** An integer split into whole and fraction at `places` digits, trailing zeros of the fraction dropped. */
function decimalOf(value: number, places: number): string {
  const text = String(Math.abs(value)).padStart(places + 1, '0');
  if (places === 0) return text;
  const whole = text.slice(0, -places);
  const fraction = text.slice(-places).replace(/0+$/, '');
  return fraction === '' ? whole : `${whole}.${fraction}`;
}

/**
 * A quantity in base units → its parts in the most natural unit: grams and millilitres past 1 000 (either way of
 * zero) as kg and L, exactly (3 255 g is "3.255" kg, never a rounded 3.26); everything else in its base unit.
 */
export function quantityParts(quantity: number, baseUnit: StockBaseUnit): QuantityParts {
  if (!Number.isSafeInteger(quantity)) throw new TypeError(`A stock quantity is a whole number of base units: got ${quantity}`);
  const negative = quantity < 0;
  if (baseUnit !== 'each' && Math.abs(quantity) >= 1000) return { negative, digits: decimalOf(quantity, 3), unit: largeUnitOf(baseUnit) };
  return { negative, digits: String(Math.abs(quantity)), unit: baseUnit };
}

/** The parts in a given unit, exactly — for a column that keeps one unit, or an input's starting text. */
export function quantityIn(quantity: number, unit: DisplayUnit): QuantityParts {
  const places = FACTOR[unit] === 1000 ? 3 : 0;
  return { negative: quantity < 0, digits: decimalOf(quantity, places), unit };
}

/** The text an input starts with for a quantity in base units, typed in `unit` ("5" for 5 000 g in kg; "" for null). */
export function inputTextOf(quantity: number | null | undefined, unit: DisplayUnit): string {
  if (quantity === null || quantity === undefined) return '';
  const parts = quantityIn(quantity, unit);
  return `${parts.negative ? '-' : ''}${parts.digits}`;
}

/**
 * Typed text in `unit` → whole base units, or `null` when it is not a quantity: a non-negative decimal with at most
 * as many fraction digits as the unit allows (three for kg and L, none for g, ml and whole units). No float: the
 * fraction is padded to three digits and the string is read as an integer.
 */
export function parseQuantity(text: string, unit: DisplayUnit): number | null {
  const trimmed = text.trim().replace(/,/g, '');
  const match = /^(\d+)(?:\.(\d*))?$/.exec(trimmed);
  if (match === null) return null;
  const whole = match[1] ?? '0';
  const fraction = match[2] ?? '';
  const places = FACTOR[unit] === 1000 ? 3 : 0;
  if (fraction.length > places && !/^0*$/.test(fraction.slice(places))) return null;
  const padded = fraction.slice(0, places).padEnd(places, '0');
  const value = Number(`${whole}${padded}`);
  return Number.isSafeInteger(value) ? value : null;
}

/** Round `numerator ÷ denominator` (both whole, denominator positive) half away from zero — the platform's one rule. */
export function roundHalfAway(numerator: bigint, denominator: bigint): bigint {
  if (denominator <= 0n) throw new RangeError('A unit cost is for a positive quantity.');
  const negative = numerator < 0n;
  const magnitude = negative ? -numerator : numerator;
  const quotient = (magnitude * 2n + denominator) / (denominator * 2n);
  return negative ? -quotient : quotient;
}

/**
 * An exact unit cost shown per display unit: ₹200 for 5 000 g is ₹40.00 per kg (`20 000 × 1 000 ÷ 5 000`), rounded
 * half away from zero once — display only, never stored.
 */
export function unitCostPer(cost: StockUnitCost, baseUnit: StockBaseUnit): { readonly price: Money; readonly per: DisplayUnit } {
  const per = baseUnit === 'each' ? 'each' : largeUnitOf(baseUnit);
  const amount = roundHalfAway(BigInt(cost.amount.amount) * BigInt(FACTOR[per]), BigInt(cost.quantity));
  return { price: { amount: Number(amount), currency: cost.amount.currency }, per };
}

/** A pack's size in base units, shown in the natural unit: "5 kg", "24". */
export function packSize(pack: Pick<StockItemPack, 'quantity'>, baseUnit: StockBaseUnit): QuantityParts {
  return quantityParts(pack.quantity, baseUnit);
}

/** What some packs and a loose quantity come to in base units — or `null` when a count is not a whole number. */
export function totalOf(packs: readonly { readonly size: number; readonly count: number }[], loose: number): number | null {
  const total = packs.reduce((sum, pack) => sum + pack.size * pack.count, loose);
  return Number.isSafeInteger(total) && total >= 0 ? total : null;
}
